import {
  LOCALE_STORAGE_KEY,
  MIVO_LOCALES,
  type MivoTranslationKey,
} from "./ITranslation";

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function isAllowedLocale(value: string): value is MivoTranslationKey {
  return (MIVO_LOCALES as readonly string[]).includes(value);
}

function resolveCookieDomain(): string | null {
  try {
    const configured = (import.meta.env.VITE_LOCALE_COOKIE_DOMAIN as string | undefined)?.trim();
    return configured && configured.length > 0 ? configured : null;
  } catch {
    return null;
  }
}

function isSecureContext(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.location.protocol === "https:";
}

function parseLocaleCookie(rawCookie: string): MivoTranslationKey | null {
  const parts = rawCookie.split(";");
  for (const part of parts) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf("=");
    if (eq <= 0) {
      continue;
    }
    const name = trimmed.slice(0, eq).trim();
    if (name !== LOCALE_STORAGE_KEY) {
      continue;
    }
    const value = decodeURIComponent(trimmed.slice(eq + 1).trim());
    if (isAllowedLocale(value)) {
      return value;
    }
    return null;
  }
  return null;
}

/** Reads shared locale cookie. Invalid values are ignored (E-01 / api-contract §8.1). */
export function readLocaleCookie(): MivoTranslationKey | null {
  if (typeof document === "undefined") {
    return null;
  }

  try {
    return parseLocaleCookie(document.cookie);
  } catch {
    return null;
  }
}

/**
 * Writes shared first-party cookie when `VITE_LOCALE_COOKIE_DOMAIN` is set (BR-14, FR-11).
 * Skips write when domain is unset (host-only cookie would not sync across origins).
 */
export function writeLocaleCookie(locale: MivoTranslationKey): void {
  if (typeof document === "undefined") {
    return;
  }

  const domain = resolveCookieDomain();
  if (!domain) {
    return;
  }

  if (!isAllowedLocale(locale)) {
    return;
  }

  const segments = [
    `${LOCALE_STORAGE_KEY}=${encodeURIComponent(locale)}`,
    "Path=/",
    `Max-Age=${ONE_YEAR_SECONDS}`,
    "SameSite=Lax",
  ];
  if (isSecureContext()) {
    segments.push("Secure");
  }
  segments.push(`Domain=${domain}`);

  try {
    document.cookie = segments.join("; ");
  } catch {
    /* ignore private mode / blocked cookies (E-11) */
  }
}

export function clearLocaleCookie(): void {
  if (typeof document === "undefined") {
    return;
  }

  const domain = resolveCookieDomain();
  if (!domain) {
    return;
  }

  const segments = [
    `${LOCALE_STORAGE_KEY}=`,
    "Path=/",
    "Max-Age=0",
    "SameSite=Lax",
    `Domain=${domain}`,
  ];
  if (isSecureContext()) {
    segments.push("Secure");
  }

  try {
    document.cookie = segments.join("; ");
  } catch {
    /* ignore */
  }
}
